'use client';

import { useEffect } from 'react';
import { faXmark } from '@fortawesome/pro-light-svg-icons';
import { faCircleCheck, faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { tv } from 'tailwind-variants';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import {
  CART_COUPON_NOTICE_DISMISS_LABEL,
  CART_COUPON_NOTICES,
  CartCouponNoticeVariant,
} from './cart-coupon.const';

import { Icon } from '@/components/ui/icon.component';
import { CartCouponNoticeKind } from '@/types/cart.types';

const noticeStyles = tv({
  slots: {
    container: 'flex w-full items-start gap-3 rounded-2xl border p-3 shadow-floating',
    icon: 'h-6 w-6 shrink-0',
    message: 'flex-1 text-base font-semibold leading-6',
    dismiss: 'shrink-0 rounded-sm transition-opacity hover:opacity-70 focus-visible:outline-none focus-visible:ring-2',
  },
  variants: {
    variant: {
      success: {
        container: 'border-success-300 bg-success-100',
        icon: 'text-success-600',
        message: 'text-success-700',
        dismiss: 'text-success-700 focus-visible:ring-success-500',
      },
      warning: {
        container: 'border-warning-300 bg-warning-100',
        icon: 'text-warning-600',
        message: 'text-warning-800',
        dismiss: 'text-warning-800 focus-visible:ring-warning-500',
      },
    },
  },
});

const iconByVariant: Record<CartCouponNoticeVariant, typeof faCircleCheck> = {
  success: faCircleCheck,
  warning: faCircleExclamation,
};

interface CartCouponNoticeProps {
  kind: CartCouponNoticeKind;
  onDismiss: () => void;
}

export function CartCouponNotice({ kind, onDismiss }: CartCouponNoticeProps) {
  const { variant, message, autoDismissMs } = CART_COUPON_NOTICES[kind];
  const { container, icon, message: messageStyle, dismiss } = noticeStyles({ variant });

  useEffect(() => {
    if (!autoDismissMs) {
      return undefined;
    }

    const timer = setTimeout(onDismiss, autoDismissMs);

    return () => clearTimeout(timer);
  }, [kind, autoDismissMs, onDismiss]);

  return (
    <div className={container()} data-testid={DataTestIdEnum.CART_COUPON_NOTICE}>
      <Icon icon={iconByVariant[variant]} className={icon()} />

      <p className={messageStyle()}>{message}</p>

      <button
        type="button"
        aria-label={CART_COUPON_NOTICE_DISMISS_LABEL}
        className={dismiss()}
        onClick={onDismiss}
        data-testid={DataTestIdEnum.CART_COUPON_NOTICE_DISMISS_BUTTON}
      >
        <Icon icon={faXmark} className="h-5 w-5" />
      </button>
    </div>
  );
}
